import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import { getEnv } from '@/lib/env';
import { safeRedirect, DEFAULT_AUTHENTICATED_REDIRECT } from '@/lib/auth/safe-redirect';
import type { Locale } from '@/i18n/routing';
import type { SignupInput } from '@/lib/schemas/auth/signup.schema';
import type { Attribution } from '@/lib/schemas/attribution.schema';

export type SignUpResult = {
  requiresEmailConfirmation: boolean;
  userId: string | null;
};

export type SignUpFetcherInput = SignupInput & {
  // Chemin interne où renvoyer l'utilisateur après confirmation email.
  // Validé via `safeRedirect` côté fetcher pour empêcher toute redirection
  // ouverte (lien email = vecteur classique de phishing OAuth-like).
  returnTo?: string;
  // Attribution marketing (UTM) lue côté serveur par la page signup. Copiée
  // dans `raw_user_meta_data.attribution` : `enrichUserAfterAuth` s'en sert
  // quand le cookie `mx_attrib` manque (confirmation email ouverte dans un
  // autre navigateur que celui de l'inscription).
  attribution?: Attribution | null;
};

export async function signUp(input: SignUpFetcherInput, locale: Locale): Promise<SignUpResult> {
  const supabase = createSupabaseBrowserClient();
  const env = getEnv();

  // Construit l'URL de callback avec le `next` propagé. Le auth callback lit
  // `?next=` et redirige l'utilisateur après l'exchange du code OAuth, ce qui
  // permet à un signup démarré depuis `/invite/<token>` de revenir sur la page
  // d'invitation après confirmation email — sans cette propagation, l'invitation
  // était silencieusement perdue.
  const next = safeRedirect(input.returnTo, DEFAULT_AUTHENTICATED_REDIRECT);
  const callbackUrl = new URL(`${env.NEXT_PUBLIC_APP_URL}/auth/callback`);
  if (next !== DEFAULT_AUTHENTICATED_REDIRECT) {
    callbackUrl.searchParams.set('next', next);
  }

  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      emailRedirectTo: callbackUrl.toString(),
      data: {
        first_name: input.firstName,
        last_name: input.lastName,
        full_name: `${input.firstName} ${input.lastName}`.trim(),
        // `locale` est stocké dans `raw_user_meta_data` et lu par GoTrue dans
        // les templates email via `{{ .Data.locale }}` pour choisir la langue.
        locale,
        ...(input.attribution ? { attribution: input.attribution } : {}),
      },
    },
  });

  if (error) throw error;

  // Supabase renvoie session === null quand la confirmation email est requise.
  return {
    requiresEmailConfirmation: !data.session,
    userId: data.user?.id ?? null,
  };
}
